/**
 * Base Service Classes
 * Based on auth-system.md specification
 *
 * Provides base classes for building services with consistent error handling,
 * logging, and timeout management.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { createBrowserClient } from "@/lib/supabase/client";
import type { ServiceResult, ServiceOptions } from "@/lib/supabase/types";
import { debugLog } from "@/utils/debug";

export const DEFAULT_SERVICE_TIMEOUT = 30000;

/**
 * Abstract base for all services
 * Provides common logging and result handling utilities
 */
export abstract class BaseService {
  protected readonly serviceName: string;

  constructor(name: string) {
    this.serviceName = name;
  }

  protected log(message: string, data?: Record<string, unknown>): void {
    debugLog(this.serviceName, message, data ?? "");
  }

  protected logError(message: string, error: unknown): void {
    if (this.isExpectedSessionMissing(error)) {
      return;
    }

    console.error(`[${this.serviceName}] ${message}`, error);
  }

  private isExpectedSessionMissing(error: unknown): boolean {
    if (!error || typeof error !== "object") {
      return false;
    }

    const maybeError = error as { name?: string; message?: string };
    const name = (maybeError.name || "").toLowerCase();
    const message = (maybeError.message || "").toLowerCase();

    return (
      name.includes("authsessionmissing") ||
      message.includes("auth session missing")
    );
  }

  protected success<T>(data: T): ServiceResult<T> {
    return { data, error: null, success: true };
  }

  protected failure<T>(error: Error | string): ServiceResult<T> {
    const errorObj = typeof error === "string" ? new Error(error) : error;
    return { data: null, error: errorObj, success: false };
  }

  protected async executeWithClient<T>(
    operation: (client: SupabaseClient) => Promise<T>,
    client: SupabaseClient,
    options?: ServiceOptions,
  ): Promise<ServiceResult<T>> {
    const timeout = options?.timeout ?? DEFAULT_SERVICE_TIMEOUT;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const result = await operation(client);
        clearTimeout(timeoutId);
        return this.success(result);
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          return this.failure(
            new Error(`Operation timed out after ${timeout}ms`),
          );
        }
        throw error;
      }
    } catch (error) {
      this.logError("Operation failed", error);
      return this.failure(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }
}

/**
 * Client-side base service (uses singleton browser client)
 * Use this for all client-side services that need Supabase access
 */
export abstract class ClientService extends BaseService {
  private _client: SupabaseClient | null = null;
  private _clientChecked = false;

  protected async getClient(): Promise<SupabaseClient | null> {
    if (this._clientChecked) return this._client;

    this._client = createBrowserClient();
    this._clientChecked = true;
    return this._client;
  }

  /**
   * Execute an operation with the Supabase client
   * Handles client creation, error handling, and timeouts
   */
  protected async execute<T>(
    operation: (client: SupabaseClient) => Promise<T>,
    options?: ServiceOptions,
  ): Promise<ServiceResult<T>> {
    const client = await this.getClient();

    if (!client) {
      return this.failure(
        new Error(
          "Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY (or VITE_SUPABASE_ANON_KEY) environment variables.",
        ),
      );
    }

    return this.executeWithClient(operation, client, options);
  }
}

// Re-export types for convenience
export type { ServiceResult, ServiceOptions };
